import React, { useEffect, useState, useRef } from "react";
import { onAuthStateChanged } from "firebase/auth";
import { auth, db } from "../firebase";
import {
    doc,
    setDoc,
    onSnapshot,
    serverTimestamp,
    collection,
    addDoc,
    deleteDoc
} from "firebase/firestore";

function exportCsv(data, headers, filename = "table.csv") {
    if (!headers.length) return;
    const rows = [
        headers.join(","),
        ...data.map(row => headers.map(h => JSON.stringify(row[h] ?? "")).join(","))
    ];
    const blob = new Blob([rows.join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
}

export default function UserSheet() {
    const [user, setUser] = useState(null);
    const [tableList, setTableList] = useState([]);
    const [selectedTableId, setSelectedTableId] = useState(null);

    const [categories, setCategories] = useState([]);
    const [countries, setCountries] = useState([]);
    const [rows, setRows] = useState([]);
    const [tableName, setTableName] = useState("");
    const [pendingTableName, setPendingTableName] = useState("");
    const [loading, setLoading] = useState(false);

    const [newCategory, setNewCategory] = useState("");
    const [countriesInput, setCountriesInput] = useState("");
    const [saving, setSaving] = useState(false);
    const [creatingTable, setCreatingTable] = useState(false);
    const [newTableName, setNewTableName] = useState("");

    const [open, setOpen] = useState(false);
    const [minimized, setMinimized] = useState(false);

    // Save debounce timer ref & typing state
    const saveDebounce = useRef(null);
    const lastTypingTime = useRef(0);
    const isSavingInProgress = useRef(false);

    useEffect(() => {
        const unsub = onAuthStateChanged(auth, setUser);
        return () => unsub();
    }, []);

    useEffect(() => {
        if (!user) return;
        setLoading(true);
        const tablesColRef = collection(db, "users", user.uid, "tables");
        const unsub = onSnapshot(tablesColRef, snap => {
            const arr = snap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            setTableList(arr);
            if (!selectedTableId && arr.length > 0) setSelectedTableId(arr[0].id);
            if (arr.length === 0) setSelectedTableId(null);
            setLoading(false);
        });
        return () => unsub();
    }, [user, selectedTableId]);

    useEffect(() => {
        if (!user || !selectedTableId) {
            setCategories([]);
            setCountries([]);
            setRows([]);
            setTableName("");
            setPendingTableName("");
            return;
        }
        setLoading(true);
        const sheetRef = doc(db, "users", user.uid, "tables", selectedTableId);
        const unsub = onSnapshot(sheetRef, snap => {
            if (snap.exists()) {
                const d = snap.data();
                setCategories(Array.isArray(d.categories) ? d.categories : []);
                setCountries(Array.isArray(d.countries) ? d.countries : []);
                setRows(Array.isArray(d.rows) ? d.rows : []);
                setTableName(d.name || "");
                setPendingTableName(d.name || "");
            }
            setLoading(false);
        });
        return () => unsub();
    }, [user, selectedTableId]);

    const handleCreateTable = async () => {
        if (!newTableName.trim() || !user) return;
        setSaving(true);
        try {
            const tablesColRef = collection(db, "users", user.uid, "tables");
            const ref = await addDoc(tablesColRef, {
                name: newTableName.trim(),
                categories: [],
                countries: [],
                rows: [],
                updatedAt: serverTimestamp()
            });
            setNewTableName("");
            setCreatingTable(false);
            setSelectedTableId(ref.id);
        } finally {
            setSaving(false);
        }
    };

    const handleRenameTable = async () => {
        if (!user || !selectedTableId || !pendingTableName.trim() || pendingTableName.trim() === tableName) return;
        setSaving(true);
        try {
            const sheetRef = doc(db, "users", user.uid, "tables", selectedTableId);
            await setDoc(sheetRef, { name: pendingTableName.trim(), updatedAt: serverTimestamp() }, { merge: true });
        } finally {
            setSaving(false);
        }
    };

    const handleDeleteTable = async () => {
        if (!user || !selectedTableId) return;
        if (!window.confirm("Delete this table? All data will be lost.")) return;
        setSaving(true);
        try {
            // Delete the selected table
            await deleteDoc(doc(db, "users", user.uid, "tables", selectedTableId));

            // Find remaining tables after deletion
            const remainingTables = tableList.filter(tbl => tbl.id !== selectedTableId);

            if (remainingTables.length > 0) {
                // Set selectedTableId to first remaining table's id
                setSelectedTableId(remainingTables[0].id);
            } else {
                // No tables left, clear selection and show create UI
                setSelectedTableId(null);
                setCreatingTable(true); // show new table creation UI
            }

            setMinimized(false);
        } finally {
            setSaving(false);
        }
    };

    const handleAddCountries = async () => {
        if (!user || !selectedTableId) return;
        let inputCountries = countriesInput.split(",").map(s => s.trim()).filter(Boolean);
        if (inputCountries.length === 0) return;
        const mergedCountries = Array.from(new Set([...countries, ...inputCountries]));
        mergedCountries.sort((a, b) => a.localeCompare(b));
        const newRows = mergedCountries.map(cname => {
            const found = rows.find(r => r.country === cname);
            if (found) return found;
            const obj = { country: cname };
            categories.forEach(cat => { obj[cat] = ""; });
            return obj;
        });
        setSaving(true);
        try {
            const sheetRef = doc(db, "users", user.uid, "tables", selectedTableId);
            await setDoc(sheetRef, {
                countries: mergedCountries,
                rows: newRows,
                updatedAt: serverTimestamp()
            }, { merge: true });
            setCountriesInput("");
        } finally {
            setSaving(false);
        }
    };

    const handleAddCategory = async () => {
        const name = newCategory.trim();
        if (!name || categories.includes(name) || !user || !selectedTableId) return;
        setSaving(true);
        try {
            const newRows = rows.map(r => ({ ...r, [name]: "" }));
            const sheetRef = doc(db, "users", user.uid, "tables", selectedTableId);
            await setDoc(sheetRef, {
                categories: [...categories, name],
                rows: newRows,
                updatedAt: serverTimestamp()
            }, { merge: true });
            setNewCategory("");
        } finally {
            setSaving(false);
        }
    };

    const handleDeleteCategory = async (cat) => {
        if (!user || !selectedTableId || !categories.includes(cat)) return;
        const filteredCats = categories.filter(c => c !== cat);
        const updatedRows = rows.map(row => {
            const copy = { ...row };
            delete copy[cat];
            return copy;
        });
        setSaving(true);
        try {
            const sheetRef = doc(db, "users", user.uid, "tables", selectedTableId);
            await setDoc(sheetRef, {
                categories: filteredCats,
                rows: updatedRows,
                updatedAt: serverTimestamp()
            }, { merge: true });
        } finally {
            setSaving(false);
        }
    };

    // Auto-save after 2 seconds of inactivity once typing stops
    const handleEditCell = (rowIndex, cat, value) => {
        setRows(prev => {
            const newRows = [...prev];
            newRows[rowIndex] = { ...newRows[rowIndex], [cat]: value };
            return newRows;
        });
        lastTypingTime.current = Date.now();
        scheduleSave();
    };

    // Schedule save with 2 seconds debounce rejecting new schedules if less than 2s from last typing
    const scheduleSave = () => {
        if (isSavingInProgress.current) return; // Reject new save if one is in progress

        if (saveDebounce.current) clearTimeout(saveDebounce.current);

        const timeSinceLastTyping = Date.now() - lastTypingTime.current;

        if (timeSinceLastTyping >= 2000) {
            saveNow();
        } else {
            const delay = 2000 - timeSinceLastTyping;
            saveDebounce.current = setTimeout(saveNow, delay);
        }
    };

    // Actual save method
    const saveNow = async () => {
        if (!user || !selectedTableId) return;
        isSavingInProgress.current = true;
        setSaving(true);
        try {
            const sheetRef = doc(db, "users", user.uid, "tables", selectedTableId);
            await setDoc(sheetRef, {
                categories,
                rows,
                updatedAt: serverTimestamp()
            }, { merge: true });
        } catch (e) {
            console.error("Auto-save failed:", e);
        }
        setSaving(false);
        isSavingInProgress.current = false;
    };

    // Flush save on unmount (component or tableId/user change)
    useEffect(() => {
        return () => {
            if (saveDebounce.current) {
                clearTimeout(saveDebounce.current);
                saveNow();
            }
        };
        // eslint-disable-next-line
    }, [user, selectedTableId]);

    const handleDeleteRow = async (rowIndex) => {
        if (!user || !selectedTableId) return;
        const ctryName = rows[rowIndex].country;
        const updatedCountries = countries.filter(c => c !== ctryName);
        const updatedRows = rows.filter((_, idx) => idx !== rowIndex);
        setSaving(true);
        try {
            const sheetRef = doc(db, "users", user.uid, "tables", selectedTableId);
            await setDoc(sheetRef, {
                countries: updatedCountries,
                rows: updatedRows,
                updatedAt: serverTimestamp()
            }, { merge: true });
        } finally {
            setSaving(false);
        }
    };

    const handleExportCsv = () => {
        const headers = ["Countries", ...categories];
        exportCsv(rows.map(r => ({ Countries: r.country, ...r })), headers, (tableName || "user_table") + ".csv");
    };

    if (!user) return null;

    return (
        <>
            {/* Minimized: both the round icon and minimized card are shown at bottom-left */}
            {minimized && (
                <>
                    <div
                        className="fixed bottom-24 left-6 z-50"
                        style={{ minWidth: 130, maxWidth: 220 }}
                        title="Minimized Table Info"
                    >
                        <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-lg border border-gray-200 dark:border-gray-800 px-4 py-2 flex flex-col min-w-[110px] select-none">
                            <div className="font-semibold text-base truncate flex items-center text-gray-800 dark:text-gray-100">
                                {tableName || "My Table"}
                                <button
                                    onClick={e => {
                                        e.stopPropagation();
                                        setMinimized(false);
                                        setOpen(true);
                                    }}
                                    className="ml-2 px-1 py-0.5 rounded hover:bg-blue-50 dark:hover:bg-blue-900 transition text-blue-600 dark:text-blue-400 text-lg"
                                    title="Maximize"
                                    aria-label="Maximize"
                                    type="button"
                                >
                                    ⬆️
                                </button>
                                <button
                                    onClick={e => {
                                        e.stopPropagation();
                                        setOpen(false);
                                        setMinimized(false);
                                    }}
                                    className="px-1 py-0.5 rounded hover:bg-red-50 dark:hover:bg-red-900 transition text-red-600 dark:text-red-400 text-lg"
                                    title="Close"
                                    aria-label="Close"
                                    type="button"
                                >
                                    ✕
                                </button>
                            </div>
                            <div className="text-sm text-gray-500 dark:text-gray-400 truncate">
                                {rows.length} rows • {categories.length} columns
                            </div>
                        </div>
                    </div>
                    <button
                        className="fixed bottom-6 left-6 w-14 h-14 rounded-full bg-gradient-to-r from-green-500 to-blue-500 text-white shadow-lg flex items-center justify-center z-50 border-4 border-white dark:border-gray-900"
                        onClick={() => { setMinimized(false); setOpen(true); }}
                        title="Open Table"
                        aria-label="Open Table"
                        type="button"
                        style={{ padding: 0 }}
                    >
                        <span role="img" aria-label="Chart Icon" style={{ fontSize: 28, userSelect: 'none' }}>
                            📊
                        </span>
                    </button>
                </>
            )}

            {/* Floating round icon only if fully closed */}
            {!open && !minimized && (
                <button
                    onClick={() => {
                        setOpen(true);
                        setMinimized(false);
                    }}
                    className="fixed bottom-6 left-6 w-14 h-14 rounded-full bg-gradient-to-r from-green-500 to-blue-500 text-white shadow-lg flex items-center justify-center z-50 hover:from-green-700 hover:to-blue-700 transition-all duration-200 border-4 border-white dark:border-gray-900"
                    title="Open Tables"
                    aria-label="Open Tables"
                    type="button"
                    style={{ padding: 0 }}
                >
                    <span role="img" aria-label="Chart Icon" style={{ fontSize: 38, userSelect: 'none', pointerEvents: 'none', display: 'inline-block', lineHeight: 1 }}>
                        📊
                    </span>
                </button>
            )}

            {/* The main modal dialog */}
            {open && !minimized && (
                <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
                    <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl max-w-7xl w-full h-full max-h-[95vh] p-6 flex flex-col relative border border-gray-200 dark:border-gray-800">
                        {/* Header */}
                        <header className="flex items-center gap-4 justify-between mb-3">
                            <select
                                value={selectedTableId || ""}
                                onChange={(e) => setSelectedTableId(e.target.value)}
                                className="border rounded px-3 py-2 font-semibold max-w-[240px] bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100 border-gray-300 dark:border-gray-700"
                            >
                                {tableList.map((tbl) => (
                                    <option value={tbl.id} key={tbl.id} className="bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100">
                                        {tbl.name || "Untitled Table"}
                                    </option>
                                ))}
                            </select>
                            <div className="flex gap-2 ml-2 flex-wrap">
                                <button
                                    onClick={() => setCreatingTable(true)}
                                    className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 dark:hover:bg-blue-800 transition"
                                    type="button"
                                >
                                    + New Table
                                </button>
                                {selectedTableId && (
                                    <button
                                        onClick={handleDeleteTable}
                                        className="bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700 dark:hover:bg-red-800 transition"
                                        type="button"
                                    >
                                        Delete Table
                                    </button>
                                )}
                                <button
                                    onClick={handleExportCsv}
                                    className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700 dark:hover:bg-green-800 transition"
                                    title="Export CSV"
                                    type="button"
                                >
                                    Export CSV
                                </button>
                            </div>
                            <div className="flex gap-2 items-center">
                                <input
                                    type="text"
                                    value={pendingTableName}
                                    onChange={(e) => setPendingTableName(e.target.value)}
                                    className="border-b-2 border-dotted px-2 py-1 font-semibold text-lg max-w-xs bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100 border-gray-300 dark:border-gray-700"
                                    style={{ minWidth: 180 }}
                                    disabled={saving}
                                    aria-label="Rename Table"
                                />
                                <button
                                    onClick={handleRenameTable}
                                    className="bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-100 px-3 py-1 rounded disabled:bg-gray-100 dark:disabled:bg-gray-800"
                                    disabled={
                                        pendingTableName === tableName || !pendingTableName.trim() || saving
                                    }
                                    type="button"
                                >
                                    Rename
                                </button>
                                <button
                                    onClick={() => setMinimized(true)}
                                    className="text-gray-600 dark:text-gray-300 text-2xl font-bold px-3"
                                    aria-label="Minimize"
                                    title="Minimize"
                                    disabled={saving}
                                    type="button"
                                >
                                    −
                                </button>
                                <button
                                    onClick={() => {
                                        setOpen(false);
                                        setMinimized(false);
                                    }}
                                    className="text-gray-600 dark:text-gray-300 text-2xl font-bold px-3"
                                    aria-label="Close"
                                    title="Close"
                                    disabled={saving}
                                    type="button"
                                >
                                    ✕
                                </button>
                            </div>
                        </header>

                        {creatingTable && (
                            <div className="mb-4 p-4 border border-gray-200 dark:border-gray-700 rounded bg-gray-50 dark:bg-gray-800 max-w-md">
                                <label className="block mb-2 font-semibold text-gray-700 dark:text-gray-100">New Table Name:</label>
                                <div className="flex gap-2">
                                    <input
                                        type="text"
                                        value={newTableName}
                                        onChange={(e) => setNewTableName(e.target.value)}
                                        className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-700 rounded bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-100"
                                        disabled={saving}
                                        autoFocus
                                    />
                                    <button
                                        onClick={handleCreateTable}
                                        disabled={!newTableName.trim() || saving}
                                        className="bg-blue-600 text-white px-4 rounded disabled:opacity-50 hover:bg-blue-700 dark:hover:bg-blue-800 transition"
                                        type="button"
                                    >
                                        Create
                                    </button>
                                    <button
                                        onClick={() => setCreatingTable(false)}
                                        className="px-4 rounded border border-gray-300 dark:border-gray-700 hover:bg-gray-200 dark:hover:bg-gray-800 text-gray-700 dark:text-gray-100"
                                        disabled={saving}
                                        type="button"
                                    >
                                        Cancel
                                    </button>
                                </div>
                            </div>
                        )}

                        <section className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-4xl">
                            <div className="flex gap-2">
                                <input
                                    type="text"
                                    value={countriesInput}
                                    onChange={(e) => setCountriesInput(e.target.value)}
                                    placeholder="Add countries (comma separated)"
                                    className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-700 rounded bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-100"
                                    disabled={saving}
                                    spellCheck={false}
                                    autoComplete="off"
                                />
                                <button
                                    onClick={handleAddCountries}
                                    disabled={!countriesInput.trim() || saving}
                                    className="bg-blue-600 text-white px-4 rounded disabled:opacity-50 hover:bg-blue-700 dark:hover:bg-blue-800 transition"
                                    type="button"
                                >
                                    Add Countries
                                </button>
                            </div>
                            <div className="flex gap-2">
                                <input
                                    type="text"
                                    value={newCategory}
                                    onChange={(e) => setNewCategory(e.target.value)}
                                    placeholder="Add category"
                                    className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-700 rounded bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-100"
                                    disabled={saving}
                                    spellCheck={false}
                                    autoComplete="off"
                                />
                                <button
                                    onClick={handleAddCategory}
                                    disabled={!newCategory.trim() || categories.includes(newCategory) || saving}
                                    className="bg-green-600 text-white px-4 rounded disabled:opacity-50 hover:bg-green-700 dark:hover:bg-green-800 transition"
                                    type="button"
                                >
                                    Add Category
                                </button>
                            </div>
                        </section>

                        <div className="flex-grow overflow-auto mt-4 max-w-full">
                            {loading ? (
                                <div className="text-center py-12 text-gray-600 dark:text-gray-300 text-lg">Loading...</div>
                            ) : tableList.length === 0 ? (
                                <div className="p-6 flex flex-col items-center justify-center gap-6 min-h-[300px] h-full">
                                    <h2 className="text-2xl font-bold text-gray-700 dark:text-gray-100 text-center">
                                        Welcome! Create your first table
                                    </h2>
                                    <input
                                        type="text"
                                        placeholder="Table name"
                                        value={newTableName}
                                        onChange={(e) => setNewTableName(e.target.value)}
                                        className="px-3 py-2 border border-gray-300 dark:border-gray-700 rounded w-72 focus:outline-none focus:ring focus:border-blue-300 dark:bg-gray-900 dark:text-gray-100"
                                        disabled={saving}
                                    />
                                    <div className="flex gap-4">
                                        <button
                                            onClick={handleCreateTable}
                                            disabled={!newTableName.trim() || saving}
                                            className="bg-blue-600 text-white px-6 py-2 rounded hover:bg-blue-700 dark:hover:bg-blue-800 disabled:opacity-50 transition"
                                            type="button"
                                        >
                                            Create Table
                                        </button>
                                        <button
                                            onClick={() => {
                                                setOpen(false);
                                                setMinimized(false);
                                            }}
                                            className="px-6 py-2 border border-gray-300 dark:border-gray-700 rounded hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-700 dark:text-gray-100"
                                            disabled={saving}
                                            type="button"
                                        >
                                            Close
                                        </button>
                                    </div>
                                </div>
                            ) : (
                                <table
                                    className="border-collapse mx-auto"
                                    style={{ border: "3px solid black", minWidth: "700px", tableLayout: "auto" }}
                                >
                                    <thead>
                                        <tr>
                                            <th
                                                className="border border-black bg-gray-100 dark:bg-gray-800 font-semibold text-gray-800 dark:text-gray-100"
                                                style={{ minWidth: 120, textAlign: "left", padding: "8px 12px" }}
                                            >
                                                Countries
                                            </th>
                                            {categories.map((cat) => (
                                                <th
                                                    key={cat}
                                                    className="border border-black bg-gray-100 dark:bg-gray-800 font-semibold text-gray-800 dark:text-gray-100 relative"
                                                    style={{ minWidth: 140, maxWidth: 200, padding: "8px 12px", textAlign: "left" }}
                                                >
                                                    {cat}
                                                    <button
                                                        onClick={() => handleDeleteCategory(cat)}
                                                        className="absolute top-1 right-1 text-red-600 dark:text-red-400 text-xs px-1 rounded hover:bg-red-100 dark:hover:bg-red-900"
                                                        title="Delete Category"
                                                        type="button"
                                                    >
                                                        ✕
                                                    </button>
                                                </th>
                                            ))}
                                            <th className="border border-black bg-gray-100 dark:bg-gray-800" style={{ width: 50 }}></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {rows.map((row, i) => (
                                            <tr key={row.country || i} className="hover:bg-gray-50 dark:hover:bg-gray-900">
                                                <td
                                                    className="border border-black font-semibold bg-gray-50 dark:bg-gray-900 text-gray-800 dark:text-gray-100"
                                                    style={{ padding: "8px 12px", minWidth: 120, textAlign: "left" }}
                                                >
                                                    {row.country}
                                                </td>
                                                {categories.map((cat) => (
                                                    <td className="border border-black bg-white dark:bg-gray-800" style={{ padding: "6px 10px" }} key={cat}>
                                                        <input
                                                            value={row[cat] ?? ""}
                                                            onChange={(e) => handleEditCell(i, cat, e.target.value)}
                                                            className="w-full outline-none bg-transparent px-1 py-0.5 text-base text-gray-800 dark:text-gray-100"
                                                            disabled={saving}
                                                            spellCheck={false}
                                                            autoComplete="off"
                                                        />
                                                    </td>
                                                ))}
                                                <td
                                                    className="border border-black text-center bg-white dark:bg-gray-800"
                                                    style={{ width: 50 }}
                                                >
                                                    <button
                                                        onClick={() => handleDeleteRow(i)}
                                                        className="text-red-600 dark:text-red-400 font-bold text-lg hover:bg-red-100 dark:hover:bg-red-900 rounded px-2"
                                                        disabled={saving}
                                                        title="Delete Row"
                                                        type="button"
                                                    >
                                                        ✕
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                        {rows.length === 0 && (
                                            <tr>
                                                <td colSpan={categories.length + 2} className="text-center p-4 text-gray-500 dark:text-gray-400 bg-white dark:bg-gray-900">
                                                    No rows yet — add countries to start.
                                                </td>
                                            </tr>
                                        )}
                                        {rows.length < 6 &&
                                            Array(6 - rows.length)
                                                .fill(0)
                                                .map((_, idx) => (
                                                    <tr key={"pad" + idx}>
                                                        <td className="border border-black p-3 bg-white dark:bg-gray-900">&nbsp;</td>
                                                        {categories.map((cat) => (
                                                            <td key={cat} className="border border-black p-3 bg-white dark:bg-gray-900">
                                                                &nbsp;
                                                            </td>
                                                        ))}
                                                        <td className="border border-black p-3 bg-white dark:bg-gray-900">&nbsp;</td>
                                                    </tr>
                                                ))}
                                    </tbody>
                                </table>
                            )}
                        </div>

                        {saving && (
                            <div className="mt-2 text-green-700 dark:text-green-400 font-semibold text-center select-none">Saving...</div>
                        )}
                    </div>
                </div>
            )}
        </>
    );
}